import { apiClient, API_PATHS } from '@/services/api';
import analyticsService from './analyticsService';

/**
 * Service for managing user bookmarks
 */
class BookmarkService {
    /**
     * Get all bookmarks for the current user
     * @param {number} limit - Number of bookmarks to fetch (default: 50)
     * @param {number} offset - Offset for pagination (default: 0)
     * @returns {Promise<Object>} Paginated bookmarks response with items and pagination info
     */
    async getBookmarks(limit = 50, offset = 0) {
        try {
            const startTime = Date.now();
            const response = await apiClient.rfp.get(API_PATHS.rfp.userBookmarks.list, {
                params: { limit, offset },
                timeout: 10000
            });

            analyticsService.trackApiCall(
                '/user-bookmarks',
                'GET',
                response.status,
                Date.now() - startTime
            );

            return response.data;
        } catch (error) {
            console.error('Error fetching bookmarks:', error);
            analyticsService.trackError(
                'api_error',
                `Failed to fetch bookmarks: ${error.message}`,
                'BookmarkService.getBookmarks'
            );
            throw error;
        }
    }


    /**
     * Create a new bookmark
     * @param {Object} bookmarkData - Bookmark data (title, path, description, icon)
     * @returns {Promise<Object>} Created bookmark
     */
    async createBookmark(bookmarkData) {
        try {
            const startTime = Date.now();
            const response = await apiClient.rfp.post(
                API_PATHS.rfp.userBookmarks.list,
                bookmarkData,
                { timeout: 10000 }
            );

            analyticsService.trackApiCall(
                '/user-bookmarks',
                'POST',
                response.status,
                Date.now() - startTime
            );

            analyticsService.trackEvent('bookmark_created', {
                path: bookmarkData.path,
                has_description: !!bookmarkData.description
            });

            return response.data.data;
        } catch (error) {
            console.error('Error creating bookmark:', error);
            analyticsService.trackError(
                'bookmark_create_error',
                `Failed to create bookmark: ${error.message}`,
                'BookmarkService.createBookmark'
            );
            throw error;
        }
    }

    /**
     * Update an existing bookmark
     * @param {string} id - Bookmark ID
     * @param {Object} updateData - Data to update
     * @returns {Promise<Object>} Updated bookmark
     */
    async updateBookmark(id, updateData) {
        try {
            const startTime = Date.now();
            
            // Filter out undefined values to prevent Firestore errors
            const filteredUpdateData = Object.keys(updateData).reduce((acc, key) => {
                if (updateData[key] !== undefined) {
                    acc[key] = updateData[key];
                }
                return acc;
            }, {});
            
            const response = await apiClient.rfp.put(
                API_PATHS.rfp.userBookmarks.byId(id),
                filteredUpdateData,
                { timeout: 10000 }
            );

            analyticsService.trackApiCall(
                `/user-bookmarks/${id}`,
                'PUT',
                response.status,
                Date.now() - startTime
            );

            analyticsService.trackEvent('bookmark_updated', {
                bookmark_id: id,
                updated_fields: Object.keys(updateData)
            });

            return response.data.data;
        } catch (error) {
            console.error(`Error updating bookmark ${id}:`, error);
            analyticsService.trackError(
                'bookmark_update_error',
                `Failed to update bookmark ${id}: ${error.message}`,
                'BookmarkService.updateBookmark'
            );
            throw error;
        }
    }

    /**
     * Delete a bookmark
     * @param {string} id - Bookmark ID
     * @returns {Promise<void>}
     */
    async deleteBookmark(id) {
        try {
            const startTime = Date.now();
            const response = await apiClient.rfp.delete(
                API_PATHS.rfp.userBookmarks.byId(id),
                { timeout: 10000 }
            );

            analyticsService.trackApiCall(
                `/user-bookmarks/${id}`,
                'DELETE',
                response.status,
                Date.now() - startTime
            );

            analyticsService.trackEvent('bookmark_deleted', {
                bookmark_id: id
            });

            return response.data;
        } catch (error) {
            console.error(`Error deleting bookmark ${id}:`, error);
            analyticsService.trackError(
                'bookmark_delete_error',
                `Failed to delete bookmark ${id}: ${error.message}`,
                'BookmarkService.deleteBookmark'
            );
            throw error;
        }
    }

    /**
     * Reorder bookmarks
     * @param {Array<string>} bookmarkIds - Ordered array of bookmark IDs
     * @returns {Promise<Array>} Updated bookmarks
     */
    async reorderBookmarks(bookmarkIds) {
        try {
            const startTime = Date.now();
            const response = await apiClient.rfp.post(
                API_PATHS.rfp.userBookmarks.reorder,
                { bookmarkIds },
                { timeout: 10000 }
            );

            analyticsService.trackApiCall(
                '/user-bookmarks/reorder',
                'POST',
                response.status,
                Date.now() - startTime
            );

            analyticsService.trackEvent('bookmarks_reordered', {
                count: bookmarkIds.length
            });

            return response.data.data || [];
        } catch (error) {
            console.error('Error reordering bookmarks:', error);
            analyticsService.trackError(
                'bookmark_reorder_error',
                `Failed to reorder bookmarks: ${error.message}`,
                'BookmarkService.reorderBookmarks'
            );
            throw error;
        }
    }

    /**
     * Track bookmark access
     * @param {string} id - Bookmark ID
     * @returns {Promise<void>}
     */
    async trackAccess(id) {
        try {
            // Don't wait for this to complete
            apiClient.rfp.post(
                API_PATHS.rfp.userBookmarks.access(id),
                {},
                { timeout: 10000 }
            ).catch(err => {
                console.warn(`Failed to track bookmark access for ${id}:`, err);
            });
        } catch (error) {
            // Silently fail - tracking is not critical
            console.warn(`Error tracking bookmark access for ${id}:`, error);
        }
    }

    /**
     * Bookmark the current page
     * @param {Object} route - Vue Router route object
     * @param {string} customTitle - Optional custom title
     * @returns {Promise<Object>} Created bookmark
     */
    async bookmarkCurrentPage(route, customTitle = null) {
        const bookmarkData = {
            title: customTitle || route.meta?.title || route.name || 'Untitled Page',
            path: route.path,
            icon: route.meta?.icon || 'fa-bookmark'
        };

        return this.createBookmark(bookmarkData);
    }
}

// Create and export a singleton instance
export const bookmarkService = new BookmarkService();
export default bookmarkService;
